import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, Save, CheckCircle2, XCircle, Clock, FileWarning } from "lucide-react";

const STATUSES = [
  { v: "present", label: "Présent", icon: CheckCircle2, color: "#059669", bg: "#ECFDF5" },
  { v: "absent", label: "Absent", icon: XCircle, color: "#E60000", bg: "#FEF2F2" },
  { v: "retard", label: "Retard", icon: Clock, color: "#D97706", bg: "#FFFBEB" },
  { v: "excuse", label: "Excusé", icon: FileWarning, color: "#0EA5E9", bg: "#EFF6FF" },
];

export default function EducationPresences() {
  const today = new Date().toISOString().slice(0, 10);
  const [eleves, setEleves] = useState([]);
  const [classes, setClasses] = useState([]);
  const [classeId, setClasseId] = useState("all");
  const [date, setDate] = useState(today);
  const [presences, setPresences] = useState({}); // eleve_id -> { status, notes, id? }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [e, c] = await Promise.all([
        api.get("/chef/education/eleves"),
        api.get("/chef/education/classes"),
      ]);
      setEleves(e.data);
      setClasses(c.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const loadPresences = async () => {
    try {
      const r = await api.get(`/chef/education/presences?date=${date}`);
      const map = {};
      (r.data || []).forEach((p) => { map[p.eleve_id] = { status: p.status, notes: p.notes || "", id: p.id }; });
      setPresences(map);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { loadPresences(); /* eslint-disable-next-line */ }, [date]);

  const filtered = useMemo(() => {
    if (classeId === "all") return eleves;
    if (classeId === "none") return eleves.filter((e) => !e.classe_id);
    return eleves.filter((e) => e.classe_id === classeId);
  }, [eleves, classeId]);

  const setStatus = (eleve_id, status) => {
    setPresences((p) => ({ ...p, [eleve_id]: { ...(p[eleve_id] || {}), status } }));
  };
  const setNotes = (eleve_id, notes) => {
    setPresences((p) => ({ ...p, [eleve_id]: { ...(p[eleve_id] || {}), notes } }));
  };

  const markAll = (status) => {
    const map = { ...presences };
    filtered.forEach((e) => { map[e.id] = { ...(map[e.id] || {}), status }; });
    setPresences(map);
  };

  const save = async () => {
    setSaving(true);
    let ok = 0, fail = 0;
    for (const eleve of filtered) {
      const p = presences[eleve.id];
      if (!p?.status) continue;
      try {
        await api.post("/chef/education/presences", {
          eleve_id: eleve.id,
          date,
          status: p.status,
          notes: p.notes || "",
        });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setSaving(false);
    if (fail === 0) toast.success(`Présences enregistrées (${ok})`);
    else toast.error(`${ok} enregistrées, ${fail} erreurs`);
    loadPresences();
  };

  const stats = useMemo(() => {
    const counts = { present: 0, absent: 0, retard: 0, excuse: 0, none: 0 };
    filtered.forEach((e) => {
      const s = presences[e.id]?.status;
      if (s && counts[s] !== undefined) counts[s] += 1;
      else counts.none += 1;
    });
    return counts;
  }, [filtered, presences]);

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Éducation · Présences"
        title="Appel & présences"
        sub="Pointez chaque élève par classe et par date. Sauvegarde groupée en un clic."
        right={
          <Button onClick={save} disabled={saving} data-testid="presences-save-btn" className="h-11 bg-[#002FA7] text-white">
            <Save className="h-4 w-4 mr-2" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        }
      />

      <div className="p-6 lg:p-10 space-y-6">
        {/* Filters */}
        <div className="bg-white border border-[#DEE2E6] p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end" data-testid="presences-filters">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 mt-1" data-testid="presences-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Classe</Label>
            <Select value={classeId} onValueChange={setClasseId}>
              <SelectTrigger className="h-11 mt-1" data-testid="presences-classe"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les classes</SelectItem>
                <SelectItem value="none">Sans classe</SelectItem>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-2 justify-end">
            {STATUSES.map((s) => (
              <Button key={s.v} size="sm" variant="outline" onClick={() => markAll(s.v)} data-testid={`mark-all-${s.v}`} className="h-9" style={{ borderColor: s.color, color: s.color }}>
                <s.icon className="h-3.5 w-3.5 mr-1" /> Tous {s.label.toLowerCase()}
              </Button>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="presences-stats">
          {STATUSES.map((s) => (
            <div key={s.v} className="bg-white border border-[#DEE2E6] p-4">
              <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: s.color }}>{s.label}</div>
              <div className="font-display font-black text-3xl mt-1">{stats[s.v]}</div>
            </div>
          ))}
          <div className="bg-white border border-[#DEE2E6] p-4">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#868E96]">Non pointés</div>
            <div className="font-display font-black text-3xl mt-1">{stats.none}</div>
          </div>
        </div>

        {/* List */}
        <div className="bg-white border border-[#DEE2E6]" data-testid="presences-list">
          <div className="p-5 border-b border-[#DEE2E6] flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold">
            <ClipboardCheck className="h-4 w-4 text-[#002FA7]" /> Élèves ({filtered.length})
          </div>
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">Aucun élève dans cette sélection.</div>
          ) : filtered.map((e) => {
            const cur = presences[e.id]?.status;
            return (
              <div key={e.id} className="p-4 border-b border-[#F8F9FA] last:border-b-0 grid grid-cols-12 gap-3 items-center hover:bg-[#F8F9FA]" data-testid={`presence-row-${e.id}`}>
                <div className="col-span-12 md:col-span-3">
                  <div className="font-bold">{e.prenom} {e.nom}</div>
                  <div className="text-[10px] text-[#868E96]">{e.classe_nom || "Sans classe"}</div>
                </div>
                <div className="col-span-12 md:col-span-5 flex flex-wrap gap-1">
                  {STATUSES.map((s) => {
                    const active = cur === s.v;
                    return (
                      <button
                        key={s.v}
                        type="button"
                        onClick={() => setStatus(e.id, s.v)}
                        data-testid={`presence-${e.id}-${s.v}`}
                        className="px-3 h-9 text-xs uppercase tracking-wider font-bold border transition-colors"
                        style={{
                          background: active ? s.color : s.bg,
                          color: active ? "#fff" : s.color,
                          borderColor: s.color,
                        }}
                      >
                        <s.icon className="h-3 w-3 inline mr-1" /> {s.label}
                      </button>
                    );
                  })}
                </div>
                <div className="col-span-12 md:col-span-4">
                  <Input
                    placeholder="Note (motif…)"
                    value={presences[e.id]?.notes || ""}
                    onChange={(ev) => setNotes(e.id, ev.target.value)}
                    className="h-9 text-xs"
                    data-testid={`presence-note-${e.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
